import { useMemo, useState } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  appliedCampaignPlacementAdjustments,
  CAMPAIGN_BID_ADJUSTMENT_PLACEMENTS,
  campaignPlacementBidPercentsFromRaw,
  displayBidPercent,
  formatCampaignPlacementPreview,
  parseAmazonBusinessBidPercent,
  parseCampaignAudienceBidAdjustments,
  signedBidPercent,
} from '@/utils/spAdGroupCreate';

export function CampaignBidAdjustmentsPanel({
  campaignRaw,
  defaultBid,
  biddingStrategyLabel,
}: {
  campaignRaw?: Record<string, unknown>;
  defaultBid: string;
  biddingStrategyLabel: string;
}) {
  const [tab, setTab] = useState<'placements' | 'audiences'>('placements');
  const percents = useMemo(
    () => campaignPlacementBidPercentsFromRaw(campaignRaw),
    [campaignRaw]
  );
  const businessPercent = useMemo(
    () => parseAmazonBusinessBidPercent(campaignRaw),
    [campaignRaw]
  );
  const audiences = useMemo(
    () => parseCampaignAudienceBidAdjustments(campaignRaw),
    [campaignRaw]
  );
  const applied = appliedCampaignPlacementAdjustments(percents);
  const preview = formatCampaignPlacementPreview(defaultBid, percents);

  return (
    <div className="space-y-4">
      <p className="text-xs text-foreground-muted">
        By raising your bids, you’ll be more likely to appear in specific placements and in front of
        specific audiences.
      </p>
      <p className="text-xs text-foreground-subtle">
        Taken from this campaign. Bid adjustments cannot be changed when creating an ad group.
      </p>

      <div className="flex gap-5 border-b border-border">
        <button
          type="button"
          className={cn(
            'relative -mb-px pb-2 text-sm font-medium',
            tab === 'placements'
              ? 'text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-brand'
              : 'text-foreground-muted'
          )}
          onClick={() => setTab('placements')}
        >
          Placements {applied.length}
        </button>
        <button
          type="button"
          className={cn(
            'relative -mb-px inline-flex items-center gap-1.5 pb-2 text-sm font-medium',
            tab === 'audiences'
              ? 'text-foreground after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-brand'
              : 'text-foreground-muted'
          )}
          onClick={() => setTab('audiences')}
        >
          Audiences
          <span className="rounded-full bg-brand/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
            New
          </span>
        </button>
      </div>

      {tab === 'placements' ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="space-y-6">
            <div>
              <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-foreground">
                Increase your bid for specific Amazon placements.
                <Info className="h-3.5 w-3.5 text-foreground-muted" aria-hidden />
              </p>
              <dl className="space-y-2">
                {CAMPAIGN_BID_ADJUSTMENT_PLACEMENTS.map((row) => (
                  <div key={row.id} className="flex items-baseline justify-between gap-4 text-sm">
                    <dt className="text-foreground">{row.label}</dt>
                    <dd className="tabular-nums text-foreground">{displayBidPercent(percents[row.id])}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-3 text-xs text-foreground-muted">{preview}</p>
            </div>
            <div>
              <p className="mb-3 text-sm font-semibold text-foreground">
                Further increase bids across placements on Amazon Business
              </p>
              <div className="flex items-baseline justify-between gap-4 text-sm">
                <span className="text-foreground">Amazon Business placements</span>
                <span className="tabular-nums text-foreground">
                  {displayBidPercent(businessPercent)}
                </span>
              </div>
              <p className="mt-3 text-xs text-foreground-muted">{preview}</p>
            </div>
          </div>

          <aside className="h-fit rounded-xl border border-border bg-surface-muted p-4">
            <p className="mb-3 flex items-center gap-1 text-sm font-medium text-brand">
              <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
              {applied.length} {applied.length === 1 ? 'adjustment' : 'adjustments'} applied
            </p>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
              Placement
            </p>
            {applied.length === 0 ? (
              <p className="mb-4 text-sm text-foreground-muted">No placement increases on this campaign.</p>
            ) : (
              <ul className="mb-4 space-y-2">
                {applied.map((row) => (
                  <li key={row.id} className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-foreground">{row.label}</span>
                    <span className="shrink-0 tabular-nums text-foreground">
                      {signedBidPercent(row.percentage)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
              Bidding strategy
            </p>
            <p className="text-sm text-foreground">{biddingStrategyLabel}</p>
          </aside>
        </div>
      ) : (
        <div className="space-y-3">
          {audiences.length === 0 ? (
            <p className="text-sm text-foreground-muted">
              No audience bid adjustments on this campaign.
            </p>
          ) : (
            <dl className="space-y-2">
              {audiences.map((row) => (
                <div
                  key={row.audienceId}
                  className="flex items-baseline justify-between gap-4 text-sm"
                >
                  <dt className="text-foreground">{row.audienceId}</dt>
                  <dd className="tabular-nums text-foreground">{signedBidPercent(row.percentage)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </div>
  );
}
